/**
 * @fileoverview PurchasePuffin Privacy Policy registry data.
 *
 * Hand-migrated faithfully from
 * `apps/purchase-web/src/portals/marketing-website/pages/PrivacyPolicyPage.jsx`.
 *
 * Includes the e-commerce-specific Third-Party Services (#4) section
 * referencing Stripe + USPS + UPS + TaxJar + SendGrid integrations.
 */

/** @type {import('../../types.js').LegalDocument} */
export const privacy = Object.freeze({
  appKey: 'purchase',
  documentKey: 'privacy',
  frontmatter: {
    title: 'Privacy Policy',
    productName: 'PurchasePuffin',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2026-02-28',
    contactEmail: 'hello@purchasepuffin.com',
    canonicalUrl: 'https://purchasepuffin.com/privacy',
    ogImage: 'https://purchasepuffin.com/og-image.jpg',
    description:
      'PurchasePuffin privacy policy. Learn how we collect, use, and protect your data including e-commerce and payment information.',
  },
  sections: [
    {
      id: 'introduction',
      title: '1. Introduction',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our e-commerce platform services.',
        },
      ],
    },
    {
      id: 'information-we-collect',
      title: '2. Information We Collect',
      order: 2,
      blocks: [
        { type: 'paragraph', text: 'We may collect the following types of information:' },
        {
          type: 'list',
          items: [
            {
              strong: 'Contact Information',
              text: 'Name, email address, company name, and other details you provide through our contact form.',
            },
            {
              strong: 'Commerce Data',
              text: 'Product catalog, order details, customer records, and pricing information managed through our platform.',
            },
            {
              strong: 'Usage Data',
              text: 'Browser type, IP address, pages visited, and other standard web analytics information.',
            },
            {
              strong: 'Payment Data',
              text: 'Payment transactions processed through our Stripe integration. We do not store full credit card numbers.',
            },
          ],
        },
      ],
    },
    {
      id: 'how-we-use-your-information',
      title: '3. How We Use Your Information',
      order: 3,
      blocks: [
        { type: 'paragraph', text: 'We use the information we collect to:' },
        {
          type: 'list',
          items: [
            'Provide and maintain our e-commerce platform services',
            'Process orders, payments, and shipping through integrated services',
            'Respond to your contact form submissions',
            'Improve our services and user experience',
            'Calculate taxes and validate shipping addresses',
            'Send you service-related communications',
          ],
        },
      ],
    },
    {
      id: 'third-party-services',
      title: '4. Third-Party Services',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: 'We integrate with Stripe for payment processing, USPS and UPS for shipping, TaxJar for tax calculation, and SendGrid for email communications. When you use these integrated services, your data is subject to both this Privacy Policy and the respective third-party privacy policies.',
        },
      ],
    },
    {
      id: 'data-security',
      title: '5. Data Security',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: 'We implement industry-standard security measures to protect your data, including encrypted connections (TLS), secure authentication (JWT), and comprehensive audit logging. However, no method of electronic transmission or storage is 100% secure.',
        },
      ],
    },
    {
      id: 'data-retention',
      title: '6. Data Retention',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'Contact form submissions are retained for two years. Commerce data and order records are retained in accordance with applicable regulatory requirements. You may request deletion of your data by contacting us.',
        },
      ],
    },
    {
      id: 'your-rights',
      title: '7. Your Rights',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'Depending on your location, you may have the right to access, correct, delete, or restrict the processing of your personal data. To exercise these rights, please contact us at {contactEmail}.',
        },
      ],
    },
    {
      id: 'changes-to-this-policy',
      title: '8. Changes to This Policy',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page with an updated effective date.',
        },
      ],
    },
    {
      id: 'contact-us',
      title: '9. Contact Us',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about this Privacy Policy, please contact us at {contactEmail}.',
        },
        { type: 'contact' },
      ],
    },
  ],
});
